import { Button } from '@app/components/various/Button';
import { ShoppingBag } from '@app/components/various/Icons';
import { compiledPaths } from '@app/paths';
import { useRequestInvoicePayment } from '@app/services/hooks/payments/useRequestInvoicePayment';
import { createAbsoluteUrl } from '@app/utils/createAbsoluteUrl';
import { isDefined } from '@app/utils/is';
import { Invoice, Payments } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InvoicePaymentMethods } from './InvoicePaymentMethods';

interface Props {
  invoiceIdentifiers: Invoice.InvoiceIdentifiers;
  paymentMethods: Payments.PaymentMethod[];
}

export const InvoicePaymentForm = ({ paymentMethods, invoiceIdentifiers }: Props) => {
  const [activeMethod, setActiveMethod] = React.useState<Nullable<string>>(paymentMethods[0]?.paymentMethod ?? null);
  const { t } = useTranslation(['invoices']);
  const [isProcessingPayment, setIsProcessingPayment] = React.useState(false);
  const { mutate: requestInvoicePayment } = useRequestInvoicePayment();
  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!isDefined(activeMethod)) {
        return;
      }
      setIsProcessingPayment(true);
      requestInvoicePayment(
        {
          identifiers: invoiceIdentifiers,
          payment: {
            cancelUrl: window.location.href,
            paymentMethod: activeMethod,
            successUrl: createAbsoluteUrl(compiledPaths.INVOICE_PAYMENT_PROCESSING(invoiceIdentifiers)),
          },
        },
        {
          onError: () => setIsProcessingPayment(false),
        },
      );
    },
    [activeMethod, invoiceIdentifiers],
  );

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      <InvoicePaymentMethods methods={paymentMethods} onChange={setActiveMethod} selected={activeMethod} />
      <Button type="submit" isLoading={isProcessingPayment} icon={ShoppingBag} className="mx-auto w-full max-w-80">
        {isProcessingPayment ? t('invoices:requesting_payment') : t('invoices:pay_now')}
      </Button>
    </form>
  );
};
